import { Controller } from "stimulus";

const COPIED_DURATION = 1800;

function copyViaTextarea(text, button) {
  const container = button.closest(".modal") || document.body;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;";
  container.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch (error) {
    copied = false;
  }
  textarea.remove();
  button.focus({ preventScroll: true });

  if (!copied) window.prompt("Copy to clipboard:", text);
}

function writeToClipboard(text, button) {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => copyViaTextarea(text, button));
  } else {
    copyViaTextarea(text, button);
  }
}

export default class extends Controller {
  static targets = [
    "tab",
    "panel",
    "picker",
    "pickerButton",
    "pickerCurrent",
    "pickerMenu",
    "pickerOption",
    "embedTitle",
    "embedCode",
    "embedCodeButton",
    "embedUrl",
    "embedLinkButton",
    "embedLocations",
    "formRow",
    "formsQuery",
    "formsCount",
    "formsEmpty",
    "formsEmptyQuery",
  ];

  disconnect() {
    this.clearCopied();
    this.resetConnecting();
    this.closePicker();
  }

  selectTab(event) {
    const selected = event.currentTarget.dataset.tab;

    [...this.tabTargets, ...this.pickerOptionTargets].forEach((tab) => {
      const active = tab.dataset.tab === selected;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active);
    });
    this.pickerCurrentTargets.forEach((current) => {
      current.hidden = current.dataset.tab !== selected;
    });
    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== selected;
    });
    this.closePicker();

    const url = new URL(window.location.href);
    url.searchParams.set("tab", selected);
    url.searchParams.delete("filter");
    window.history.replaceState(window.history.state, "", url.toString());
  }

  togglePicker() {
    if (this.pickerMenuTarget.hidden) {
      this.openPicker();
    } else {
      this.closePicker();
    }
  }

  openPicker() {
    this.pickerMenuTarget.hidden = false;
    this.pickerButtonTarget.setAttribute("aria-expanded", "true");

    this.boundPickerOutside = (event) => {
      if (!this.pickerTarget.contains(event.target)) this.closePicker();
    };
    this.boundPickerKeydown = (event) => {
      if (event.key !== "Escape") return;
      this.closePicker();
      this.pickerButtonTarget.focus();
    };
    document.addEventListener("click", this.boundPickerOutside);
    document.addEventListener("keydown", this.boundPickerKeydown);
  }

  closePicker() {
    if (this.boundPickerOutside) document.removeEventListener("click", this.boundPickerOutside);
    if (this.boundPickerKeydown) document.removeEventListener("keydown", this.boundPickerKeydown);
    this.boundPickerOutside = null;
    this.boundPickerKeydown = null;
    if (!this.hasPickerMenuTarget) return;

    this.pickerMenuTarget.hidden = true;
    this.pickerButtonTarget.setAttribute("aria-expanded", "false");
  }

  copy(event) {
    const button = event.currentTarget;
    writeToClipboard(button.dataset.copyText, button);

    this.clearCopied();
    button.classList.add("is-copied");
    this.copiedButton = button;
    this.copiedTimer = setTimeout(() => this.clearCopied(), COPIED_DURATION);

    if (button.dataset.copyToast && window.notyf) {
      window.notyf.success({ message: button.dataset.copyToast, duration: COPIED_DURATION + 400 });
    }
  }

  clearCopied() {
    clearTimeout(this.copiedTimer);
    if (this.copiedButton) this.copiedButton.classList.remove("is-copied");
    this.copiedButton = null;
  }

  prepareEmbed(event) {
    const { embedName, embedCode, embedUrl, embedDisplayUrl, embedLocations } = event.currentTarget.dataset;

    this.clearCopied();
    this.embedTitleTarget.textContent = `Embed ${embedName}`;
    this.embedCodeTarget.textContent = embedCode;
    this.embedCodeButtonTarget.dataset.copyText = embedCode;
    this.embedUrlTarget.textContent = embedDisplayUrl;
    this.embedLinkButtonTarget.dataset.copyText = embedUrl;
    this.embedLinkButtonTarget.dataset.copyToast = `${embedName} link copied`;

    const template = embedLocations && document.getElementById(embedLocations);
    this.embedLocationsTarget.replaceChildren(template ? template.content.cloneNode(true) : "");
    this.embedLocationsTarget.hidden = !template;
  }

  filterForms() {
    const raw = this.formsQueryTarget.value.trim();
    const query = raw.toLowerCase();
    const total = this.formRowTargets.length;
    let visible = 0;

    this.formRowTargets.forEach((row) => {
      const match = !query || row.dataset.name.includes(query);
      row.hidden = !match;
      if (match) visible += 1;
    });

    const noun = total === 1 ? "form" : "forms";
    this.formsCountTarget.textContent = query ? `${visible} of ${total} ${noun}` : `${total} ${noun}`;
    this.formsEmptyQueryTarget.textContent = raw;
    this.formsEmptyTarget.hidden = visible > 0;
  }

  startConnecting(event) {
    const link = event.currentTarget;
    link.classList.add("is-connecting");
    link.setAttribute("aria-busy", "true");
    this.connectingLink = link;

    this.boundResetConnecting = () => this.resetConnecting();
    window.addEventListener("pageshow", this.boundResetConnecting, { once: true });
  }

  resetConnecting() {
    if (this.boundResetConnecting) window.removeEventListener("pageshow", this.boundResetConnecting);
    if (!this.connectingLink) return;

    this.connectingLink.classList.remove("is-connecting");
    this.connectingLink.removeAttribute("aria-busy");
    this.connectingLink = null;
  }
}
